import React from 'react';
import { BaseTool, IToolManifest, IToolContext } from '../interfaces/ITool';
import { BaseCommand, ICommand } from '../../commands/ICommand';
import { IFrame, IFrameBounds } from '../../frames/IFrame';
import { DiagramType } from '../interfaces/ITool';
import { ToolComponentFactory } from '../components/ToolComponents';
import { Frame, Square, Users, Building } from 'lucide-react';

// Command for creating frames
export class CreateFrameCommand extends BaseCommand {
  private frame: IFrame;
  private addFrame: (frame: IFrame) => void;
  private removeFrame: (frameId: string) => void;
  
  constructor(
    frame: IFrame,
    addFrame: (frame: IFrame) => void,
    removeFrame: (frameId: string) => void
  ) {
    super('Create Frame', `Create frame "${frame.name}"`);
    this.frame = frame;
    this.addFrame = addFrame;
    this.removeFrame = removeFrame;
  }
  
  execute(): void {
    this.addFrame(this.frame);
  }
  
  undo(): void {
    this.removeFrame(this.frame.id);
  }
}

// Command for deleting frames
export class DeleteFrameCommand extends BaseCommand {
  private frame: IFrame;
  private addFrame: (frame: IFrame) => void;
  private removeFrame: (frameId: string) => void;
  
  constructor(
    frame: IFrame,
    addFrame: (frame: IFrame) => void,
    removeFrame: (frameId: string) => void
  ) {
    super('Delete Frame', `Delete frame "${frame.name}"`);
    this.frame = frame;
    this.addFrame = addFrame;
    this.removeFrame = removeFrame;
  }
  
  execute(): void {
    this.removeFrame(this.frame.id);
  }
  
  undo(): void {
    this.addFrame(this.frame);
  }
}

// Base frame tool
export abstract class FrameTool extends BaseTool {
  public isCreating = false;
  protected startPoint: { x: number; y: number } | null = null;
  protected currentBounds: IFrameBounds | null = null;
  
  getBaseManifest(): Partial<IToolManifest> {
    return {
      name: 'Frame',
      icon: '⬚',
      category: 'create',
      description: 'Create and manage frames',
      version: '1.0.0',
      
      requiresSelection: false,
      
      ui: {
        toolbar: true,
        contextMenu: true,
        propertyPanel: false
      },
      
      capabilities: {
        modifiesShapes: false,
        createsShapes: false,
        deletesShapes: false,
        modifiesConnectors: false,
        createsConnectors: false,
        deletesConnectors: false,
        requiresUserInput: true,
        supportsMultiSelect: false,
        supportsBatchOperations: false
      },
      
      shortcuts: [{
        key: 'f',
        modifiers: [],
        description: 'Create frame'
      }]
    };
  }
  
  onActivate(_context: IToolContext): void {
    super.onActivate(_context);
    this.isCreating = false;
    this.startPoint = null;
    
    // Change cursor to crosshair
    if (_context.stage) {
      _context.stage.container().style.cursor = 'crosshair';
    }
  }
  
  onDeactivate(): void {
    super.onDeactivate();
    this.isCreating = false;
    this.startPoint = null;
    
    // Reset cursor
    if (this.context?.stage) {
      this.context.stage.container().style.cursor = 'default';
    }
  }
  
  canExecute(_context: IToolContext): boolean {
    return true;
  }
  
  // Abstract methods
  abstract getFrameTypes(): { value: DiagramType; label: string; icon?: string }[];
  abstract createFrame(bounds: IFrameBounds, type: DiagramType): IFrame;
  abstract addFrame(frame: IFrame): void;
  abstract removeFrame(frameId: string): void;
  abstract getFrame(frameId: string): IFrame | undefined;
  
  execute(params: { bounds?: IFrameBounds; type?: DiagramType }): ICommand | null {
    if (!params.bounds) return null;
    
    const frame = this.createFrame(
      params.bounds,
      params.type || 'generic'
    );
    
    return new CreateFrameCommand(
      frame,
      (f) => this.addFrame(f),
      (id) => this.removeFrame(id)
    );
  }
  
  // Handle mouse events for frame creation
  handleMouseDown(point: { x: number; y: number }): void {
    this.isCreating = true;
    this.startPoint = point;
    this.currentBounds = {
      x: point.x,
      y: point.y,
      width: 0,
      height: 0
    };
  }
  
  handleMouseMove(point: { x: number; y: number }): void {
    if (!this.isCreating || !this.startPoint) return;
    
    this.currentBounds = {
      x: Math.min(this.startPoint.x, point.x),
      y: Math.min(this.startPoint.y, point.y),
      width: Math.abs(point.x - this.startPoint.x),
      height: Math.abs(point.y - this.startPoint.y)
    };
    
    // Emit event for preview rendering
    this.context?.eventBus.emit('frame:preview', this.currentBounds);
  }
  
  handleMouseUp(): IFrameBounds | null {
    if (!this.isCreating || !this.currentBounds) return null;
    
    this.isCreating = false;
    const bounds = this.currentBounds;
    this.startPoint = null;
    this.currentBounds = null;
    
    // Clear preview
    this.context?.eventBus.emit('frame:preview', null);
    
    // Minimum size check
    if (bounds.width < 50 || bounds.height < 50) {
      return null;
    }
    
    return bounds;
  }
  
  getToolbarComponent(): React.ComponentType<any> {
    return ({ onExecute }: any) => {
      const frameTypes = this.getFrameTypes();
      const [selectedType, setSelectedType] = React.useState<DiagramType>('generic');
      
      const handleFrameType = (type: string) => {
        setSelectedType(type as DiagramType);
        onExecute({ type });
      };
      
      // Map frame type icons to Lucide icons
      const getFrameIcon = (type: string) => {
        switch (type) {
          case 'genogram': return Users;
          case 'database': return Building;
          case 'flowchart': return Square;
          default: return Frame;
        }
      };
      
      return ToolComponentFactory.createSelect({
        icon: Frame,
        placeholder: 'Generic',
        value: selectedType,
        onValueChange: handleFrameType,
        options: frameTypes.map(type => ({
          value: type.value,
          label: type.label,
          icon: getFrameIcon(type.value),
          description: undefined
        }))
      });
    };
  }
}